import { WsResponse } from '@1po/1po-bff-fe-spec/generated/common/WsResponse';
import { SendEmailFromGarage } from '@1po/1po-bff-fe-spec/generated/email/request/SendEmailFromGarage';
import { SendReferenceInterestToExadisResponse } from '@1po/1po-bff-fe-spec/generated/email/response/SendReferenceInterestToExadisResponse';
import { ReturnEmailResponse } from '@1po/1po-bff-fe-spec/generated/order/response/ReturnEmailResponse';
import { call, put, takeEvery } from '@redux-saga/core/effects';
import { SagaIterator } from 'redux-saga';
import { RootState } from 'app/AppStore';
import { getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import {
  sendEmailDocumentationAlertMotrio,
  sendEmailExadisReferenceInterestRequest,
  sendEmailFromGarageRequest,
  sendEmailReturnRequestRequest,
  sendEmailToAssistanceRequest,
} from 'domains/email/Email.api';
import { sendEmailToAssistanceMapper } from 'domains/email/Email.mapper';
import { updateReturnedReferenceQty } from 'domains/order/Order.store';
import { getIAMReference } from 'domains/references';
import { getUserContext } from 'domains/user';
import { notifyTop } from 'UI/Notification/notification';
import { AppTranslation, DONE, hasData, isLoading, LOADING, sagaGuard, select } from 'utils';
import * as actions from './Email.store';
import {
  getEmailDocumentationAlertMotrioData,
  getEmailFromGarageData,
  getEmailToAssistanceData,
  setEmailDocumentationAlertMotrioDataStatus,
  setEmailToAssistanceDataStatus,
  setReturnRequestStatus,
} from './Email.store';
import { SENT } from './Email.types';

function* sendEmailFromGarageRequestSaga(): SagaIterator {
  const emailData = yield* select(getEmailFromGarageData);
  if (!isLoading(emailData.attachments)) {
    yield put(sendEmailFromGarageRequest(emailData as SendEmailFromGarage));
  }
}

export function* sendEmailReturnRequestRequestSaga({
  payload,
}: ReturnType<typeof actions.sendEmailReturnRequestRequestSaga>): SagaIterator {
  yield put(setReturnRequestStatus(LOADING));
  yield put(sendEmailReturnRequestRequest(payload));
}

export function* sendEmailReturnRequestResponseSaga(action: WsResponse<ReturnEmailResponse>): SagaIterator {
  yield put(setReturnRequestStatus(DONE));
  yield put(
    updateReturnedReferenceQty({
      orderId: action.payload.orderId,
      references: action.payload.referencesToReturn,
    }),
  );
}

function* sendEmailFromGarageResponseSaga(): SagaIterator {
  yield call(notifyTop, 'success', AppTranslation.t('email.send.success', 'Email sent'), null);
}

function* sendEmailToAssistanceRequestSaga({
  payload,
}: ReturnType<typeof actions.sendEmailToAssistanceRequestSaga>): SagaIterator {
  const emailData = sendEmailToAssistanceMapper(yield* select(getEmailToAssistanceData), payload);
  if (emailData) {
    yield put(setEmailToAssistanceDataStatus(LOADING));
    yield put(sendEmailToAssistanceRequest(emailData));
  }
}

function* sendEmailToAssistanceResponseSaga(): SagaIterator {
  yield put(setEmailToAssistanceDataStatus(SENT));
}

function* sendEmailExadisReferenceInterestRequestSaga({
  payload,
}: ReturnType<typeof actions.sendEmailExadisReferenceInterestRequestSaga>): SagaIterator {
  const userContext = yield* select(getUserContext);
  const vehicleDetail = yield* select(getLastVehicleDetail);
  const referenceDetail = yield* select((state: RootState) =>
    getIAMReference(state, { referenceNumber: payload.referenceNumber, vehicleKey: vehicleDetail?.vehicleKey }),
  );
  if (!hasData(referenceDetail)) {
    return;
  }

  yield put(
    sendEmailExadisReferenceInterestRequest({
      referenceNumber: referenceDetail.referenceNumber,
      brand: referenceDetail.brand ?? referenceDetail.supplier,
      name: referenceDetail.name ?? referenceDetail.designation,
      userContext,
    }),
  );
}

function* sendEmailExadisReferenceInterestResponseSaga(
  _action: WsResponse<SendReferenceInterestToExadisResponse>,
): SagaIterator {
  // this is correct - customer want to show success everytime (also in case of error) - response is ignored
}

function* sendEmailDocumentationAlertMotrioRequestSaga(): SagaIterator {
  const emailData = yield* select(getEmailDocumentationAlertMotrioData);
  if (
    emailData &&
    emailData.request !== undefined &&
    emailData.email !== undefined &&
    emailData.socialReason !== undefined &&
    emailData.companyRegistrationNumber !== undefined &&
    emailData.vehicleRegistrationNumber !== undefined
  ) {
    yield put(setEmailDocumentationAlertMotrioDataStatus(LOADING));
    yield put(
      sendEmailDocumentationAlertMotrio({
        ...emailData,
        request: emailData.request,
        email: emailData.email,
        socialReason: emailData.socialReason,
        companyRegistrationNumber: emailData.companyRegistrationNumber,
        vehicleRegistrationNumber: emailData.vehicleRegistrationNumber,
      }),
    );
  }
}

function* sendEmailDocumentationAlertMotrioResponseSaga(): SagaIterator {
  yield put(setEmailDocumentationAlertMotrioDataStatus(SENT));
}

export function* EmailSagas(): SagaIterator {
  yield takeEvery(
    actions.sendEmailExadisReferenceInterestRequestSaga.type,
    sagaGuard(sendEmailExadisReferenceInterestRequestSaga),
  );
  yield takeEvery(
    actions.sendEmailExadisReferenceInterestResponseSaga.type,
    sagaGuard(sendEmailExadisReferenceInterestResponseSaga),
  );
  yield takeEvery(actions.sendEmailFromGarageRequestSaga.type, sagaGuard(sendEmailFromGarageRequestSaga));
  yield takeEvery(actions.sendEmailReturnRequestRequestSaga.type, sagaGuard(sendEmailReturnRequestRequestSaga));
  yield takeEvery(actions.sendEmailFromGarageResponseSaga.type, sagaGuard(sendEmailFromGarageResponseSaga));
  yield takeEvery(actions.sendEmailReturnRequestResponseSaga.type, sagaGuard(sendEmailReturnRequestResponseSaga));
  yield takeEvery(actions.sendEmailToAssistanceRequestSaga.type, sagaGuard(sendEmailToAssistanceRequestSaga));
  yield takeEvery(actions.sendEmailToAssistanceResponseSaga.type, sagaGuard(sendEmailToAssistanceResponseSaga));
  yield takeEvery(
    actions.sendEmailDocumentationAlertMotrioRequestSaga.type,
    sagaGuard(sendEmailDocumentationAlertMotrioRequestSaga),
  );
  yield takeEvery(
    actions.sendEmailDocumentationAlertMotrioResponseSaga.type,
    sagaGuard(sendEmailDocumentationAlertMotrioResponseSaga),
  );
}
